'use client'

import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Filter, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { ProcessedTheme } from '../../ProcessedEntry/DomainEntry/processedTypes'
import { compactDate } from '../../ProcessedEntry/DomainEntry/processedTypes'
import type { CttCampaign, RawEntry } from '../trustCenterTypes'
import { BaseDashboard, baseDashboardClasses } from '../../BaseDashboard'
import { ResizableTable, TableTd, type ColumnDef } from '../../ResizableTable'
import { CttProcessedEntryDetail } from './processed_entry/CttProcessedEntryDetail'
import { CttBrandCampaignDetail } from './brand/CttBrandCampaignDetail'

type Row = { kind: 'brand'; data: CttCampaign } | { kind: 'processed'; data: RawEntry }

const CTT_TRUSTCENTER_COLUMNS: ColumnDef[] = [
  { key: 'kind', label: 'Kind', initialWidth: 100 },
  { key: 'created_at', label: 'Created_At', initialWidth: 130 },
  { key: 'headline', label: 'Headline', initialWidth: 280 },
  { key: 'sender', label: 'Sender', initialWidth: 160 },
  { key: 'message_content', label: 'Message_Content', initialWidth: 320 },
  { key: 'status', label: 'Status', initialWidth: 100 },
]

// Small top-row stat card matching the counts the old card-list view
// (CttTrustCenterView) showed above its list — same two numbers, just
// reshaped into a standalone BaseDashboard widget (its own border/rounded
// corners) instead of sharing a gap-px grid wrapper with its sibling.
function EntriesStat({ label, value, tone, theme }: { label: string; value: string; tone: 'sky' | 'amber'; theme: ProcessedTheme }) {
  const { border, panelBg, muted } = baseDashboardClasses(theme)
  const val = tone === 'sky' ? 'text-signal' : 'text-warning'
  return (
    <div className={`col-span-6 row-start-1 flex flex-col justify-center border px-4 py-3 ${border} ${panelBg}`}>
      <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>{label}</div>
      <div className={`font-mono text-[28px] leading-none tracking-tight mt-1 ${val}`}>{value}</div>
    </div>
  )
}

// Which brand's trust center this instance renders. The component started
// CTT-only; the data path is brand-agnostic (brand_campaigns.brand_name +
// the platform's processed entries), so other brands reuse it with their
// own codes — MEO since 2026-10-29. `code` is brand_campaigns.brand_name
// (matched case-insensitively), `platform` the platform table name_code.
export type TrustCenterBrand = { code: string; label: string; platform: string }
const CTT_BRAND: TrustCenterBrand = { code: 'ctt', label: 'CTT', platform: 'ctt' }
export const MEO_TRUSTCENTER_BRAND: TrustCenterBrand = { code: 'MEO', label: 'MEO', platform: 'meo' }

// Same data source and "brand campaign vs. processed entry" detail routing
// as CttTrustCenterView (the card-list mockup, now kept around as "Old
// entries") — built on BaseDashboard/ResizableTable instead so this one
// reads like every other CTT dashboard (CttMailAlertView, etc.)
// rather than its own bespoke split-pane layout.
export function CttTrustCenterEntriesView({ theme = 'dark', brand = CTT_BRAND }: { theme?: ProcessedTheme; brand?: TrustCenterBrand }) {
  const isDark = theme === 'dark'
  const { muted } = baseDashboardClasses(theme)

  const [brandCampaigns, setBrandCampaigns] = useState<CttCampaign[]>([])
  const [processedEntries, setProcessedEntries] = useState<RawEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [detailEntry, setDetailEntry] = useState<RawEntry | null>(null)
  const [detailBrand, setDetailBrand] = useState<CttCampaign | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)

  const fetchEntries = () => {
    setError(null)
    Promise.all([
      fetch(`/api/campaigns/ctt?brand=${encodeURIComponent(brand.code)}`).then((r) => r.json()),
      fetch(`/api/campaigns/ctt/unified?platform=${encodeURIComponent(brand.platform)}`).then((r) => r.json()),
    ])
      .then(([brandPayload, unifiedPayload]) => {
        if (brandPayload?.success) setBrandCampaigns(brandPayload.data.entries ?? [])
        else setError(brandPayload?.error ?? 'Failed to load brand campaigns')
        if (unifiedPayload?.success) setProcessedEntries(unifiedPayload.data.entries ?? [])
        else setError(unifiedPayload?.error ?? 'Failed to load unified entries')
        setLastUpdated(Date.now())
      })
      .catch(() => setError('Network error'))
      .finally(() => {
        setLoading(false)
        setRefreshing(false)
      })
  }

  useEffect(() => { fetchEntries() }, [])

  const handleRefresh = () => {
    setRefreshing(true)
    fetchEntries()
  }

  const allRows = useMemo<Row[]>(() => {
    const rows: Row[] = [
      ...brandCampaigns.map((d) => ({ kind: 'brand' as const, data: d })),
      ...processedEntries.map((d) => ({ kind: 'processed' as const, data: d })),
    ]
    return rows.sort((a, b) => new Date(b.data.created_at).getTime() - new Date(a.data.created_at).getTime())
  }, [brandCampaigns, processedEntries])

  const filtered = useMemo(() => {
    const q = query.toLowerCase()
    if (!q) return allRows
    return allRows.filter(({ data }) =>
      (data.headline ?? '').toLowerCase().includes(q) ||
      (data.message_content ?? '').toLowerCase().includes(q) ||
      (data.sender ?? '').toLowerCase().includes(q),
    )
  }, [allRows, query])

  if (detailEntry) {
    return (
      <CttProcessedEntryDetail
        entry={detailEntry}
        theme={theme}
        onBack={() => setDetailEntry(null)}
        onPromoted={fetchEntries}
        recentCampaigns={brandCampaigns}
        brand={brand.code}
      />
    )
  }

  if (detailBrand) {
    return (
      <CttBrandCampaignDetail
        entry={detailBrand}
        theme={theme}
        onBack={() => setDetailBrand(null)}
        onSaved={fetchEntries}
      />
    )
  }

  return (
    <BaseDashboard
      theme={theme}
      eyebrow={`${brand.label} · Trust Center`}
      title="Trust Center"
      subtitle={<>Unified view from <span className="font-mono text-[12px]">brand_campaigns_unified</span>. Promote processed entries into brand campaigns.</>}
      onRefresh={handleRefresh}
      refreshing={refreshing}
      loading={loading}
      error={error}
      onRetry={handleRefresh}
      lastUpdated={lastUpdated}
      storageKey={`${brand.platform}-trustcenter-header`}
      widgetCols={12}
      widgetRows="8rem"
      widgets={
        <>
          <EntriesStat label="Brand campaigns" value={String(brandCampaigns.length).padStart(2, '0')} tone="sky" theme={theme} />
          <EntriesStat label="Processed entries" value={String(processedEntries.length).padStart(2, '0')} tone="amber" theme={theme} />
        </>
      }
    >
      <ResizableTable
        columns={CTT_TRUSTCENTER_COLUMNS}
        stickyHeader
        theme={theme}
        isEmpty={filtered.length === 0}
        emptyMessage="No entries"
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdated={lastUpdated}
        storageKey={`${brand.platform}-trustcenter-table`}
        toolbar={
          <>
            <Search className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
            <Input
              placeholder="Filter by title, sender, message…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-7 border-0 shadow-none bg-transparent focus-visible:ring-0 px-0 text-[13px]"
            />
            <Filter className={`h-3.5 w-3.5 shrink-0 ${muted}`} />
            <span className={`font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${muted}`}>
              {filtered.length} / {allRows.length}
            </span>
          </>
        }
      >
        {filtered.map(({ kind, data }) => (
          <tr
            key={data.id}
            onClick={() => (kind === 'brand' ? setDetailBrand(data as CttCampaign) : setDetailEntry(data as RawEntry))}
            className={`border-b border-l-2 transition-colors cursor-pointer border-border ${
              kind === 'brand'
                ? 'border-l-signal hover:bg-secondary'
                : 'border-l-warning hover:bg-warning-surface/60'
            }`}
          >
            <TableTd className={`font-mono uppercase text-[10px] tracking-widest ${kind === 'brand' ? 'text-signal' : 'text-warning'}`}>
              {/* A brand campaign promoted from a processed entry (processed_entry_id
                  set) is both at once — the source processed row is deliberately
                  excluded from /api/campaigns/ctt/unified once promoted (see that
                  route), so this is the only place that duality still shows up. */}
              {kind === 'brand' ? ((data as CttCampaign).processed_entry_id ? 'Brand · Processed' : 'Brand') : 'Processed'}
            </TableTd>
            <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{compactDate(data.created_at)}</TableTd>
            <TableTd className={'text-foreground'}>
              <span className="line-clamp-1">{data.headline ?? '—'}</span>
            </TableTd>
            <TableTd className={'text-muted-foreground'}>{data.sender ?? '—'}</TableTd>
            <TableTd><span className={`line-clamp-1 text-muted-foreground`}>{data.message_content ?? '—'}</span></TableTd>
            <TableTd className={`font-mono text-[10px] text-subtle`}>
              {kind === 'brand' ? ((data as CttCampaign).is_active ? 'active' : 'inactive') : ((data as RawEntry).post_type ?? '—')}
            </TableTd>
          </tr>
        ))}
      </ResizableTable>
    </BaseDashboard>
  )
}
